import React, { useMemo, useCallback } from "react";
import {
  FormProvider,
  useForm,
  type SubmitHandler,
  type Resolver,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldRenderer } from "../../../components/form/FieldRenderer";
import { licenseCount } from "./config";
import {
  addCustomerProductLicenseFormSchema,
  AddCustomerProductLicenseFormValues,
} from "./schema";
import { SECTION_CARD_CLASS } from "../../../constants/mspAndCustomerForm";
import { AddCustomerProductLicenseFormProps } from "./types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { addLicensesToCustomerSubscription } from "../services/customerService";
export default function AddMspProductLicenseForm({
  subscription,
  onProductLicenseAdded,
  onCancel,
}: AddCustomerProductLicenseFormProps) {
  const dispatch = useAppDispatch();
  const resolver = useMemo(
    () =>
      zodResolver(
        addCustomerProductLicenseFormSchema
      ) as Resolver<AddCustomerProductLicenseFormValues>,
    []
  );
  const selectedMspId = useAppSelector(
    (state) => state.selectedMsp.selectedMspId
  );

  const selectedCustomerId = useAppSelector(
    (state) => state.selectedCustomer.selectedCustomerId
  );

  const DEFAULT_VALUES = {
    plan_price_id: undefined,
    no_of_licenses: undefined,
  };

  const methods = useForm<AddCustomerProductLicenseFormValues>({
    resolver,
    mode: "onBlur",
    shouldFocusError: false,
    shouldUnregister: false,
    defaultValues: DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = methods;

  // Form submission handler
  const onSubmit: SubmitHandler<AddCustomerProductLicenseFormValues> = useCallback(
    async (formData) => {
      try {
        const payload = {
          licenses: formData.no_of_licenses || 1,
        };
        // await addLicensesToCustomerSubscription(selectedMspId!, selectedCustomerId!, subscription?.id!, payload);
        await addLicensesToCustomerSubscription(selectedMspId!, selectedCustomerId!, subscription?.planPrice.plan.product.id!, payload);

        reset();
        onCancel && onCancel();
        onProductLicenseAdded && onProductLicenseAdded();
      } catch (error) {
        console.error("Failed to add Customer product license:", error);
      }
    },
    [dispatch, reset]
  );

  // Render functions for organization
  const renderHeader = () => (
    <div className="mb-4 flex items-start justify-between p-2">
      <div>
        <h2 className="text-[26px] font-semibold text-[#4F4F4F]">
          Add More Licenses
        </h2>
      </div>
    </div>
  );

  const renderFormSection = (title: React.ReactNode, config: any[]) => (
    <>
      <h3 className="text-[15px] font-bold text-[#2B2B2B]">{title}</h3>
      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-1">
        {config.map((f) => (
          <FieldRenderer key={f.name} cfg={f} />
        ))}
      </div>
    </>
  );

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-6"
          autoComplete="off"
        >
          {renderHeader()}

          <div className={SECTION_CARD_CLASS}>
            <div>
              {renderFormSection(
                <>
                  {subscription?.planPrice.plan.product.product_name}
                  <br />
                  <br />

                  <span className="block text-[13px] font-semibold text-[#3B3B3B]">                    
                    You currently have: {subscription?.no_of_licenses} licenses
                  </span>
                  <br />
                  <span className="block text-[13px] font-semibold text-[#3B3B3B]">

                  </span>
                </>, licenseCount)}
            </div>

            <div className="mt-8 flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex min-w-[180px] items-center justify-center rounded-[6px] bg-[#1D58E2] px-8 py-3 text-[15px] font-semibold text-white shadow hover:brightness-110 disabled:opacity-50 transition-all duration-200"
              >
                {isSubmitting ? "ADDING..." : "ADD NOW"}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
}
